import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Plus, Trash2, LogOut, KeyRound, Crown, UserX, Upload, ImagePlus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { CompanyFields, companyToForm, formToCompany, validateCompany, Field } from '@/components/forms/CompanyFields';
import { ListEditor } from '@/components/forms/ListEditor';
import { CnpjCheck } from '@/components/forms/CnpjCheck';
import { PasswordInput } from '@/pages/auth/AuthPages';
import { PageTitle } from './DashboardPages';
import { GeneratedCover } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { companyService, authService } from '@/services/api';
import { supabase } from '@/lib/supabase';
import { portfolioImageBlob } from '@/lib/portfolioImage';
import { uid } from '@/lib/format';
import { useSeo } from '@/lib/seo';

const MAX_PORTFOLIO = 6;
const MAX_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const EMPTY_PF = { title: '', description: '', imageUrl: '', linkUrl: '' };

const Loading = () => (
  <div className="surface mx-auto max-w-lg p-8 text-center text-sm text-muted-foreground">Carregando…</div>
);

/* ---------------- Perfil ---------------- */
function ProfileForm({ company }) {
  const [form, setForm] = useState(() => companyToForm(company));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const er = validateCompany(form);
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error('Revise os campos destacados.');
      return;
    }
    setBusy(true);
    try {
      await companyService.update(company.id, formToCompany(form));
      toast.success('Perfil atualizado');
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} noValidate className="max-w-3xl" data-testid="profile-form">
      <PageTitle title="Meu perfil empresarial" text="Estas informações aparecem no seu perfil público e no mapa." right={<Button type="submit" className="btn-glow" disabled={busy} data-testid="profile-save-button">{busy ? <Loader2 className="animate-spin" /> : <Save />} Salvar</Button>} />
      <div className="surface p-5 sm:p-6"><CompanyFields values={form} errors={errors} onChange={setForm} /></div>
      <CnpjCheck company={company} />
      <div className="mt-4 flex justify-end"><Button type="submit" className="btn-glow" disabled={busy}>{busy ? <Loader2 className="animate-spin" /> : <Save />} Salvar alterações</Button></div>
    </form>
  );
}

export function ProfilePage() {
  useSeo({ title: 'Meu perfil empresarial' });
  const { myCompany } = useData();
  if (!myCompany) return <Loading />;
  return <ProfileForm company={myCompany} />;
}

/* ---------------- Serviços ---------------- */
function ServicesForm({ company, userId }) {
  const [services, setServices] = useState(company.services || []);
  const [products, setProducts] = useState(company.products || []);
  const [portfolio, setPortfolio] = useState(company.portfolio || []);
  const [pf, setPf] = useState(EMPTY_PF);
  const [pfErr, setPfErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const pfInputRef = useRef(null);
  const full = portfolio.length >= MAX_PORTFOLIO;

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) return toast.error('Use uma imagem PNG, JPG ou WebP.');
    if (file.size > MAX_BYTES) return toast.error('A imagem precisa ter até 2 MB.');
    if (!userId) return toast.error('Entre na sua conta para enviar imagens.');

    setUploading(true);
    try {
      const blob = await portfolioImageBlob(file);
      const path = `${userId}/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage
        .from('company-portfolio')
        .upload(path, blob, { contentType: 'image/jpeg', cacheControl: '3600' });
      if (error) throw error;
      const { data } = supabase.storage.from('company-portfolio').getPublicUrl(path);
      setPf((p) => ({ ...p, imageUrl: data.publicUrl }));
      setPfErr((er) => ({ ...er, image: undefined }));
    } catch (ex) {
      toast.error(ex.message || 'Não foi possível enviar a imagem.');
    } finally {
      setUploading(false);
    }
  };

  const addPf = () => {
    const er = {};
    if (pf.title.trim().length < 3) er.title = 'Informe um título (mínimo 3 caracteres).';
    if (!pf.imageUrl) er.image = 'Escolha uma imagem do computador.';
    if (pf.linkUrl.trim() && !/^https:\/\/\S+$/.test(pf.linkUrl.trim())) er.link = 'O link precisa começar com https://';
    if (full) er.limit = `Você já tem ${MAX_PORTFOLIO} itens. Remova um para adicionar outro.`;
    setPfErr(er);
    if (Object.keys(er).length) return;
    setPortfolio([...portfolio, { id: uid('pf'), title: pf.title.trim(), description: pf.description.trim(), imageUrl: pf.imageUrl, linkUrl: pf.linkUrl.trim() || null }]);
    setPf(EMPTY_PF);
  };

  const save = async () => {
    setBusy(true);
    try {
      await companyService.update(company.id, { services, products, portfolio });
      toast.success('Serviços e portfólio salvos');
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="services-page">
      <PageTitle title="Meus serviços" text="Serviços, produtos e portfólio exibidos no perfil." right={<Button className="btn-glow" onClick={save} disabled={busy} data-testid="services-save-button">{busy ? <Loader2 className="animate-spin" /> : <Save />} Salvar</Button>} />
      <section className="surface p-5"><h2 className="mb-3 font-display text-base font-semibold">Serviços</h2><ListEditor id="svc" items={services} onChange={setServices} placeholder="Ex.: Implantação de ERP" testId="services-editor" /></section>
      <section className="surface p-5"><h2 className="mb-3 font-display text-base font-semibold">Produtos</h2><ListEditor id="prd" items={products} onChange={setProducts} placeholder="Ex.: Linha de congelados" testId="products-editor" /></section>
      <section className="surface p-5" data-testid="portfolio-section">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold">Portfólio</h2>
          <span className="font-mono text-xs text-muted-foreground" data-testid="portfolio-count">{portfolio.length}/{MAX_PORTFOLIO}</span>
        </div>

        {full ? (
          <p className="text-sm text-muted-foreground">Limite de {MAX_PORTFOLIO} itens atingido. Remova um item para adicionar outro.</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field id="pf-title" label="Título" error={pfErr.title}>
                <Input id="pf-title" value={pf.title} onChange={(e) => setPf({ ...pf, title: e.target.value })} data-testid="portfolio-title-input" />
              </Field>
              <Field id="pf-desc" label="Descrição curta (opcional)">
                <Input id="pf-desc" value={pf.description} onChange={(e) => setPf({ ...pf, description: e.target.value })} />
              </Field>
            </div>
            <div className="mt-3">
              <Field id="pf-img" label="Imagem do computador" error={pfErr.image} hint="PNG, JPG ou WebP até 2 MB. A imagem é ajustada automaticamente para 4:3.">
                <div className="flex flex-wrap items-center gap-3">
                  {pf.imageUrl ? (
                    <img src={pf.imageUrl} alt="Prévia do item" className="h-14 w-20 rounded-md border border-white/10 object-cover" />
                  ) : (
                    <div className="grid h-14 w-20 place-items-center rounded-md border border-dashed border-white/15 text-muted-foreground"><ImagePlus className="h-4 w-4" /></div>
                  )}
                  <Button type="button" variant="secondary" size="sm" className="border border-white/10" disabled={uploading} onClick={() => pfInputRef.current?.click()} data-testid="portfolio-upload-button">
                    {uploading ? <Loader2 className="animate-spin" /> : <Upload />} {pf.imageUrl ? 'Trocar imagem' : 'Escolher imagem'}
                  </Button>
                  <input ref={pfInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pickImage} data-testid="portfolio-file-input" />
                </div>
              </Field>
            </div>
            <div className="mt-3">
              <Field id="pf-link" label="Link do conteúdo (opcional)" error={pfErr.link} hint="Se preenchido, aparece o botão Acessar conteúdo. Use um endereço que começa com https://">
                <Input id="pf-link" value={pf.linkUrl} onChange={(e) => setPf({ ...pf, linkUrl: e.target.value })} placeholder="https://..." data-testid="portfolio-link-input" />
              </Field>
            </div>
            {pfErr.limit && <p className="mt-2 text-xs text-[rgb(255,120,140)]">{pfErr.limit}</p>}
            <Button type="button" variant="secondary" className="mt-3 border border-white/10" onClick={addPf} disabled={uploading} data-testid="portfolio-add-button"><Plus /> Adicionar item</Button>
          </>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {portfolio.map((p) => (
            <div key={p.id} className="overflow-hidden rounded-lg border border-white/10">
              {p.imageUrl ? <img src={p.imageUrl} alt={p.title} className="aspect-[4/3] w-full object-cover" /> : <GeneratedCover seed={p.title} className="aspect-[4/3]" />}
              <div className="flex items-center gap-2 bg-[#0b0f16] px-3 py-2">
                <span className="flex-1 truncate text-sm">{p.title}</span>
                {p.linkUrl && <ExternalLink className="h-3.5 w-3.5 text-primary" aria-label="Tem link" />}
                <button type="button" aria-label={`Remover ${p.title}`} onClick={() => setPortfolio(portfolio.filter((x) => x.id !== p.id))} className="text-muted-foreground hover:text-[#ff6b84]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ServicesPage() {
  useSeo({ title: 'Meus serviços' });
  const { myCompany, user } = useData();
  if (!myCompany) return <Loading />;
  return <ServicesForm company={myCompany} userId={user?.id} />;
}

/* ---------------- Configurações ---------------- */
function SettingsForm({ user, myCompany }) {
  const navigate = useNavigate();
  const [acc, setAcc] = useState({ name: user.name, email: user.email });
  const [pw, setPw] = useState({ current: '', next: '' });
  const [errs, setErrs] = useState({});

  const saveAcc = async (e) => {
    e.preventDefault();
    const er = {};
    if (acc.name.trim().length < 3) er.name = 'Informe o nome.';
    if (!/^\S+@\S+\.\S+$/.test(acc.email)) er.email = 'E-mail inválido.';
    setErrs(er);
    if (Object.keys(er).length) return;
    try {
      await authService.updateAccount({ name: acc.name.trim(), email: acc.email });
      toast.success('Dados da conta atualizados');
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8 || !/[A-Za-z]/.test(pw.next) || !/\d/.test(pw.next)) {
      setErrs({ next: 'Nova senha: mínimo 8 caracteres, com letras e números.' });
      return;
    }
    try {
      await authService.changePassword(pw.current, pw.next);
      setPw({ current: '', next: '' });
      setErrs({});
      toast.success('Senha alterada');
    } catch (ex) {
      setErrs({ current: ex.message });
    }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="settings-page">
      <PageTitle title="Configurações da conta" />
      <form onSubmit={saveAcc} noValidate className="surface grid gap-4 p-5 sm:grid-cols-2" data-testid="settings-account-form">
        <h2 className="font-display text-base font-semibold sm:col-span-2">Dados do responsável</h2>
        <Field id="set-name" label="Nome" error={errs.name}><Input id="set-name" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} data-testid="settings-name-input" /></Field>
        <Field id="set-email" label="E-mail" error={errs.email}><Input id="set-email" type="email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} data-testid="settings-email-input" /></Field>
        <div className="sm:col-span-2 flex justify-end"><Button type="submit" className="btn-glow" data-testid="settings-account-save"><Save /> Salvar</Button></div>
      </form>
      <form onSubmit={savePw} noValidate className="surface grid gap-4 p-5 sm:grid-cols-2" data-testid="settings-password-form">
        <h2 className="font-display text-base font-semibold sm:col-span-2">Alterar senha</h2>
        <Field id="set-pw-current" label="Senha atual" error={errs.current}><PasswordInput id="set-pw-current" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} testId="settings-current-password" /></Field>
        <Field id="set-pw-next" label="Nova senha" error={errs.next}><PasswordInput id="set-pw-next" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} testId="settings-new-password" /></Field>
        <div className="sm:col-span-2 flex justify-end"><Button type="submit" variant="secondary" className="border border-white/10"><KeyRound /> Alterar senha</Button></div>
      </form>
      <section className="surface p-5" data-testid="settings-plan">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold"><Crown className="h-4 w-4 text-amber-200" /> Plano</h2>
        <p className="mt-2 text-sm text-muted-foreground">Plano atual: <span className="text-foreground">{myCompany?.plan === 'premium' ? 'Premium' : 'Gratuito'}</span>. Planos premium e publicações patrocinadas ainda estão em definição. Nenhum pagamento é processado no momento.</p>
        <Button variant="secondary" className="mt-3 border border-white/10" onClick={() => toast('Interesse registrado', { description: 'Avisaremos quando os planos forem lançados.' })} data-testid="settings-plan-interest">Tenho interesse no Premium</Button>
      </section>
      <section className="surface p-5">
        <h2 className="font-display text-base font-semibold">Sessão</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" className="border border-white/10" onClick={async () => { await authService.logout(); navigate('/'); }} data-testid="settings-logout"><LogOut /> Sair</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="ghost" className="text-[rgb(255,130,150)] hover:bg-[rgba(255,59,92,0.08)]" data-testid="settings-delete-account"><UserX /> Excluir conta</Button></AlertDialogTrigger>
            <AlertDialogContent className="border-white/10 bg-[#0b0f16]"><AlertDialogHeader><AlertDialogTitle>Excluir sua conta?</AlertDialogTitle><AlertDialogDescription>A conta, a empresa cadastrada e suas publicações serão removidas. Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={async () => { try { await authService.deleteAccount(); navigate('/'); } catch (ex) { toast.error(ex.message); } }}>Excluir</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
    </div>
  );
}

export function SettingsPage() {
  useSeo({ title: 'Configurações da conta' });
  const { user, myCompany } = useData();
  if (!user) return <Loading />;
  return <SettingsForm user={user} myCompany={myCompany} />;
}